import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "ivem-ui-theme";
const VALID = ["light", "dark", "system"];

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};

/* What the OS is asking for right now. Safe in older browsers and in tests. */
function systemTheme() {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

export const ThemeProvider = ({ children }) => {
  /* The saved *preference* — light, dark, or system (follow the device).
     Light stays the out-of-the-box default so the portal opens on the
     approved Command Center look; System is a deliberate choice. */
  const [preference, setPreferenceState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return VALID.includes(saved) ? saved : "light";
    } catch {
      return "light";
    }
  });

  /* The theme actually painted — preference, or the OS when following it. */
  const [systemPref, setSystemPref] = useState(systemTheme);

  /* Follow the OS while (and only while) the preference is "system". */
  useEffect(() => {
    if (preference !== "system") return undefined;
    let mq;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return undefined;
    }
    const onChange = (e) => setSystemPref(e.matches ? "dark" : "light");
    setSystemPref(mq.matches ? "dark" : "light");
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange); // Safari < 14
    return () => mq.removeListener(onChange);
  }, [preference]);

  const theme = preference === "system" ? systemPref : preference;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    root.style.colorScheme = theme; // native scrollbars and form controls follow
  }, [theme]);

  const setPreference = useCallback((next) => {
    const value = VALID.includes(next) ? next : "light";
    setPreferenceState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* private mode — the choice just won't survive a reload */
    }
  }, []);

  /* Kept so existing callers keep working: flips between light and dark,
     stepping off "system" onto the opposite of whatever is on screen. */
  const toggleTheme = useCallback(() => {
    setPreference(theme === "dark" ? "light" : "dark");
  }, [theme, setPreference]);

  const value = useMemo(
    () => ({ theme, preference, setPreference, setTheme: setPreference, toggleTheme }),
    [theme, preference, setPreference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
